/**
 * Таблица типов тематики — главное на экране тематики: какой фасон растёт,
 * какой падает, когда пик и есть ли уже по нему референсы.
 *
 * Сортировка по умолчанию — «12 мес» по убыванию: сначала то, что ищут больше
 * всего. Пустые значения (у нового типа ещё нет истории) всегда в конце, в
 * какую бы сторону ни сортировали: иначе при сортировке по росту наверх
 * всплывали бы типы без данных.
 *
 * «Собрать» в строке ставит сбор Pinterest с настройками по умолчанию — чтобы
 * добавленные слова не обходить по одному через их страницы. Свои настройки
 * (видео, глубина, свой запрос) — на странице слова.
 *
 * Колонка «EN» — запрос для англоязычного Pinterest. Пусто — переведётся при
 * сборе пакета; владелец правит прямо в ячейке (Enter/уход из поля — сохранить,
 * Esc — отмена).
 *
 * Отброшенные типы скрыты по умолчанию: их не собирает Wordstat, и в списке
 * «что снимать» они — шум. Вернуть можно из той же строки.
 */
import { ArrowDown, ArrowUp, EyeOff, Loader2, RotateCcw, Search } from "lucide-react";
import { useMemo, useRef, useState, type ReactNode } from "react";
import { Link, useNavigate } from "react-router";

import { Sparkline } from "@/components/trends/charts";
import { btnGhost, Chip, Empty } from "@/components/trends/ui";
import { mediaUrl, num, when } from "@/lib/media";
import { monthLong, monthShort, peakCount, yoyText, yoyTone, type TrendType, type TypeState } from "@/lib/trends";

type SortKey = "name" | "grp" | "year" | "yoy" | "peak" | "refs" | "search";
type Sort = { key: SortKey; desc: boolean };

const sortValue: Record<SortKey, (t: TrendType) => string | number | null> = {
  name: (t) => t.name.toLowerCase(),
  grp: (t) => t.grp.toLowerCase(),
  year: (t) => (t.months.length ? t.year : null),
  yoy: (t) => t.yoy,
  // Пик сортируем по месяцу года (янв…дек): вопрос «когда сезон», а не «в каком году».
  peak: (t) => (t.peak_month ? Number(t.peak_month.slice(5, 7)) : null),
  refs: (t) => t.refs,
  search: (t) => t.date_search,
};

function compare(a: TrendType, b: TrendType, sort: Sort): number {
  const va = sortValue[sort.key](a);
  const vb = sortValue[sort.key](b);
  if (va === null && vb === null) return 0;
  if (va === null) return 1;
  if (vb === null) return -1;
  const d = typeof va === "number" && typeof vb === "number" ? va - vb : String(va).localeCompare(String(vb), "ru");
  return sort.desc ? -d : d;
}

export function TypesTable({
  types,
  onState,
  onCollect,
  onPhraseEn,
  collectingIds,
  pendingId,
  emptyText = "Типов пока нет — сделайте тип из фразы Wordstat ниже.",
  noun = "тип",
}: {
  types: TrendType[];
  /** Нет — смотрят чужую подборку: кнопки «отбросить/вернуть» не показываем. */
  onState?: ((id: number, state: TypeState) => void) | undefined;
  /** Сбор Pinterest по слову с настройками по умолчанию; нет — кнопки нет. */
  onCollect?: ((id: number) => void) | undefined;
  /** Правка английской фразы; нет — колонка только для чтения. */
  onPhraseEn?: ((id: number, text: string) => void) | undefined;
  /** Слова, по которым сбор Pinterest в очереди или идёт. */
  collectingIds?: ReadonlySet<number> | undefined;
  emptyText?: string;
  /** Как зовётся строка: «тип» у тематики, «слово» у подборки. */
  noun?: string;
  /** Тип, чьё состояние сейчас меняется, — его кнопка неактивна. */
  pendingId: number | null;
}) {
  const navigate = useNavigate();
  const [sort, setSort] = useState<Sort>({ key: "year", desc: true });
  const [group, setGroup] = useState<string | null>(null);
  const [showIgnored, setShowIgnored] = useState(false);
  const [editingEn, setEditingEn] = useState<number | null>(null);

  const ignoredCount = types.filter((t) => t.state === "ignored").length;
  const groups = useMemo(() => {
    const counts = new Map<string, number>();
    for (const t of types) if (showIgnored || t.state === "active") counts.set(t.grp, (counts.get(t.grp) ?? 0) + 1);
    return [...counts.entries()];
  }, [types, showIgnored]);

  const rows = useMemo(
    () =>
      types
        .filter((t) => showIgnored || t.state === "active")
        .filter((t) => group === null || t.grp === group)
        .sort((a, b) => compare(a, b, sort)),
    [types, showIgnored, group, sort],
  );

  const head = (key: SortKey, title: string, hint: string, align: "left" | "right" = "right") => {
    const on = sort.key === key;
    return (
      <th
        className={`border-b border-border px-3 py-2 whitespace-nowrap ${align === "left" ? "text-left" : "text-right"}`}
        title={hint}
        aria-sort={on ? (sort.desc ? "descending" : "ascending") : "none"}
      >
        <button
          type="button"
          onClick={() =>
            setSort((s) =>
              s.key === key ? { key, desc: !s.desc } : { key, desc: key !== "name" && key !== "grp" && key !== "peak" },
            )
          }
          className={`inline-flex items-center gap-1 hover:text-fg ${on ? "text-fg" : ""}`}
        >
          {title}
          {on ? sort.desc ? <ArrowDown size={11} /> : <ArrowUp size={11} /> : null}
        </button>
      </th>
    );
  };

  return (
    <div>
      <div className="flex flex-wrap items-center gap-1.5 border-b border-border px-3 py-2">
        <Chip on={group === null} onClick={() => setGroup(null)}>
          все группы
        </Chip>
        {groups.map(([g, n]) => (
          <Chip key={g} on={group === g} onClick={() => setGroup(group === g ? null : g)}>
            {g} <span className="tnum text-fg-faint">{n}</span>
          </Chip>
        ))}
        <span className="ml-auto" />
        {ignoredCount ? (
          <label className="inline-flex cursor-pointer items-center gap-1.5 text-[12px] text-fg-muted">
            <input type="checkbox" checked={showIgnored} onChange={(e) => setShowIgnored(e.target.checked)} />
            показать отброшенные ({ignoredCount})
          </label>
        ) : null}
      </div>

      {rows.length === 0 ? (
        <Empty>
          {types.length === 0 ? emptyText : "В этой выборке ничего нет."}
        </Empty>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-[12.5px]">
            <thead>
              <tr className="bg-bg-subtle text-[11px] font-medium text-fg-muted">
                {head("name", noun, "Название и фраза поиска", "left")}
                <th
                  className="border-b border-border px-3 py-2 text-left whitespace-nowrap"
                  title="Запрос для англоязычного Pinterest; пусто — переведётся при сборе"
                >
                  EN
                </th>
                {head("grp", "группа", "Группа типа: силуэт, длина, детали…", "left")}
                {head("year", "12 мес", "Сумма запросов за последние 12 месяцев")}
                {head("yoy", "г/г", "Последние 12 месяцев к предыдущим 12")}
                <th className="border-b border-border px-3 py-2 text-left whitespace-nowrap" title="24 месяца: бледное — прошлый год, яркое — последний">
                  24 мес
                </th>
                {head("peak", "пик", "Месяц с наибольшим числом запросов (сортировка — по месяцу года)")}
                {head("refs", "референсов", "Скачанных пинов Pinterest по типу")}
                {head("search", "Pinterest", "Когда последний раз искали референсы")}
                {onState || onCollect ? <th className="border-b border-border px-3 py-2" /> : null}
              </tr>
            </thead>
            <tbody>
              {rows.map((t) => {
                const ignored = t.state === "ignored";
                return (
                  <tr
                    key={t.id}
                    className={`cursor-pointer hover:bg-bg-hover ${ignored ? "opacity-55" : ""}`}
                    onClick={() => navigate(`/trends/type/${t.id}`)}
                  >
                    <Td align="left">
                      <Link
                        to={`/trends/type/${t.id}`}
                        onClick={(e) => e.stopPropagation()}
                        className="font-medium text-fg hover:text-accent-text"
                      >
                        {t.name}
                      </Link>
                      {ignored ? <span className="ml-1.5 text-[11px] text-fg-faint">отброшен</span> : null}
                      {t.phrase !== t.name ? <div className="text-[11.5px] text-fg-faint">{t.phrase}</div> : null}
                    </Td>
                    <Td align="left" muted>
                      {editingEn === t.id && onPhraseEn ? (
                        <PhraseEnInput
                          initial={t.phrase_en ?? ""}
                          onDone={(text) => {
                            setEditingEn(null);
                            if (text !== null && text !== (t.phrase_en ?? "")) onPhraseEn(t.id, text);
                          }}
                        />
                      ) : onPhraseEn ? (
                        <button
                          type="button"
                          className="text-left hover:text-fg"
                          title="Изменить английский запрос"
                          onClick={(e) => {
                            e.stopPropagation();
                            setEditingEn(t.id);
                          }}
                        >
                          {t.phrase_en ?? "—"}
                        </button>
                      ) : (
                        (t.phrase_en ?? "—")
                      )}
                    </Td>
                    <Td align="left" muted>
                      {t.grp}
                    </Td>
                    <Td num>{t.months.length ? num(t.year) : "—"}</Td>
                    <Td num className={yoyTone(t.yoy)}>
                      {yoyText(t.yoy)}
                    </Td>
                    <Td align="left">
                      <Sparkline months={t.months} />
                    </Td>
                    <Td num muted>
                      <span title={t.peak_month ? `${monthLong(t.peak_month)}: ${num(peakCount(t))}` : undefined}>
                        {monthShort(t.peak_month)}
                      </span>
                    </Td>
                    <Td>
                      <span className="inline-flex items-center justify-end gap-2">
                        {t.cover ? (
                          <img
                            src={mediaUrl(t.cover)}
                            alt=""
                            loading="lazy"
                            className="h-[30px] w-[22px] rounded-[3px] object-cover"
                          />
                        ) : null}
                        <span className={`tnum ${t.refs ? "" : "text-fg-faint"}`}>{num(t.refs)}</span>
                      </span>
                    </Td>
                    <Td num muted>
                      {collectingIds?.has(t.id) ? (
                        <span className="inline-flex items-center gap-1 text-warn">
                          <Loader2 size={11} className="animate-spin" />
                          идёт
                        </span>
                      ) : t.date_search ? (
                        when(t.date_search)
                      ) : (
                        <span className="text-fg-faint">не искали</span>
                      )}
                    </Td>
                    {onState || onCollect ? (
                      <Td>
                        <span className="inline-flex items-center gap-1">
                          {onCollect && !ignored ? (
                            <button
                              type="button"
                              className={btnGhost}
                              disabled={collectingIds?.has(t.id)}
                              title="Собрать с Pinterest: картинки, 15 страниц, 40 лучших. Другие настройки — на странице слова"
                              onClick={(e) => {
                                e.stopPropagation();
                                onCollect(t.id);
                              }}
                            >
                              {collectingIds?.has(t.id) ? <Loader2 size={12} className="animate-spin" /> : <Search size={12} />}
                              собрать
                            </button>
                          ) : null}
                          {onState ? (
                            <button
                              type="button"
                              className={btnGhost}
                              disabled={pendingId === t.id}
                              title={ignored ? "Вернуть тип: снова собирать его в Wordstat" : "Отбросить тип: не собирать и скрыть"}
                              onClick={(e) => {
                                e.stopPropagation();
                                onState(t.id, ignored ? "active" : "ignored");
                              }}
                            >
                              {ignored ? <RotateCcw size={12} /> : <EyeOff size={12} />}
                              {ignored ? "вернуть" : "отбросить"}
                            </button>
                          ) : null}
                        </span>
                      </Td>
                    ) : null}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/** Поле правки EN: Enter и уход из поля сохраняют, Esc отменяет (`null`). */
function PhraseEnInput({ initial, onDone }: { initial: string; onDone: (text: string | null) => void }) {
  const [value, setValue] = useState(initial);
  // Esc закрывает поле, а следом приходит blur — второй раз сохранять нельзя.
  const done = useRef(false);
  const finish = (text: string | null) => {
    if (done.current) return;
    done.current = true;
    onDone(text);
  };
  return (
    <input
      autoFocus
      value={value}
      maxLength={255}
      className="w-40 rounded-[4px] border border-border bg-bg px-1.5 py-0.5 text-[12px] text-fg"
      onClick={(e) => e.stopPropagation()}
      onChange={(e) => setValue(e.target.value)}
      onBlur={() => finish(value)}
      onKeyDown={(e) => {
        if (e.key === "Enter") finish(value);
        else if (e.key === "Escape") finish(null);
      }}
    />
  );
}

function Td({
  children,
  align = "right",
  num: isNum = false,
  muted = false,
  className = "",
}: {
  children: ReactNode;
  align?: "left" | "right";
  num?: boolean;
  muted?: boolean;
  className?: string;
}) {
  return (
    <td
      className={`border-b border-border px-3 py-1.5 align-middle whitespace-nowrap ${
        align === "left" ? "text-left" : "text-right"
      } ${isNum ? "tnum" : ""} ${muted ? "text-fg-muted" : ""} ${className}`}
    >
      {children}
    </td>
  );
}
